import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "../api";
import { useCart } from "../context/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("confirming");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="order-success-page" className="pt-40 pb-32 px-4 sm:px-8 lg:px-16 min-h-screen">
      <div className="max-w-xl mx-auto">
        {state === "confirming" && (
          <div className="text-center" data-testid="order-confirming">
            <Loader2 className="animate-spin mx-auto text-[#FF334B]" size={32} />
            <p className="mono text-xs tracking-[0.25em] uppercase text-zinc-400 mt-6">Confirming your payment…</p>
          </div>
        )}
        {state === "paid" && order && (
          <div data-testid="order-confirmation" className="border border-zinc-800 bg-[#121215] p-8 sm:p-10">
            <CheckCircle2 className="text-[#FF334B]" size={36} />
            <h1 className="font-display font-extrabold tracking-tighter uppercase text-3xl mt-6">Order Confirmed</h1>
            <p className="text-zinc-400 text-sm mt-2 font-light">
              A receipt has been sent to <span className="text-zinc-200">{order.email}</span>.
            </p>
            <div className="mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 mt-8 flex justify-between border-b border-zinc-800 pb-4">
              <span>Order</span>
              <span data-testid="order-id" className="text-zinc-300">{order.order_id?.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="py-4 border-b border-zinc-800">
              {order.items?.map((i) => (
                <div key={`${i.slug}-${i.size}`} className="flex justify-between text-sm py-2">
                  <span className="text-zinc-300">
                    {i.name} <span className="text-zinc-500">/ {i.size} × {i.qty}</span>
                  </span>
                  <span className="mono text-zinc-300">${(i.unit_price * i.qty).toFixed(0)}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-between py-4">
              <span className="mono text-xs tracking-[0.25em] uppercase text-zinc-400">Total</span>
              <span data-testid="order-total" className="mono text-lg">${order.amount?.toFixed(0)} USD</span>
            </div>
            <p className="mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">Estimated delivery: 5–7 business days</p>
            <div className="flex gap-4 mt-8">
              <Link to="/orders" data-testid="view-orders-button" className="flex-1 text-center border border-zinc-600 py-3 mono text-xs tracking-[0.25em] uppercase hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                Track Order
              </Link>
              <Link to="/shop" data-testid="continue-shopping-button" className="flex-1 text-center bg-[#FF334B] text-white py-3 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors">
                Keep Shopping
              </Link>
            </div>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div className="text-center" data-testid="order-pending-message">
            <h1 className="font-display font-bold uppercase text-2xl">Payment pending</h1>
            <p className="text-zinc-400 text-sm mt-4">
              We couldn't confirm your payment yet. Check your order status in a moment.
            </p>
            <Link to="/orders" className="mono text-xs tracking-[0.25em] uppercase text-[#FF334B] mt-6 inline-block">
              Go to Order Lookup
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
